import { useEffect } from 'react';
import { useModelStore } from '../../../../store/useModelStore';
import { useUIStore } from '../../../../store/useUIStore';

/**
 * Integration utility to ensure click selection and copy/paste work correctly
 * This ensures that all selection modes work properly without interference
 */
export const useSelectionIntegration = () => {
  const modelStore = useModelStore();
  const uiStore = useUIStore();

  useEffect(() => {
    // Clear stored selection bounds when selection is manually cleared
    const clearStoredBounds = () => {
      (window as any).__lastSelectionBounds = null;
      //console.log('🎯 Cleared stored selection bounds');
    };

    // Listen for selection changes
    const checkSelectionState = () => {
      const hasNodes = modelStore?.selectedNodeIds?.length > 0;
      const hasBeams = modelStore?.selectedBeamIds?.length > 0;
      
      if (!hasNodes && !hasBeams) {
        clearStoredBounds();
      }
    };

    // Debounced check
    const timeoutId = setTimeout(checkSelectionState, 100);
    return () => clearTimeout(timeoutId);
  }, [modelStore?.selectedNodeIds, modelStore?.selectedBeamIds]);

  // Initialize global selection utilities
  useEffect(() => {
    // Enhanced copy function with validation
    (window as any).__enhancedCopy = () => {
      const lastSelectionBounds = (window as any).__lastSelectionBounds;
      const hasSelection = (modelStore?.selectedNodeIds?.length || 0) + (modelStore?.selectedBeamIds?.length || 0) > 0;
      
      if (!hasSelection) {
        //console.log('📋 No elements selected to copy');
        if (uiStore?.showNotification) {
          uiStore.showNotification('No elements selected to copy', 'warning');
        }
        return;
      }

      if (lastSelectionBounds) {
        //console.log('📋 Using precise copy validation for drag selection');
      } else {
        //console.log('📋 Using simple copy for click selection');
      }

      // The actual copy logic is handled by the keyboard shortcut handler
      // This is just for debugging and validation
      return true;
    };

    // Enhanced paste function
    (window as any).__enhancedPaste = () => {
      //console.log('📋 Enhanced paste operation triggered');
      return true;
    };

    // Selection state debug utility
    (window as any).__getSelectionState = () => {
      return {
        selectedNodes: modelStore?.selectedNodeIds || [],
        selectedBeams: modelStore?.selectedBeamIds || [],
        lastSelectionBounds: (window as any).__lastSelectionBounds,
        totalSelected: (modelStore?.selectedNodeIds?.length || 0) + (modelStore?.selectedBeamIds?.length || 0)
      };
    };

    //console.log('🎯 Selection integration utilities initialized');

    return () => {
      // Cleanup
      (window as any).__enhancedCopy = undefined;
      (window as any).__enhancedPaste = undefined;
      (window as any).__getSelectionState = undefined;
      (window as any).__lastSelectionBounds = null;
    };
  }, [modelStore, uiStore]);

  return {
    // Utility functions for debugging
    clearStoredBounds: () => {
      (window as any).__lastSelectionBounds = null;
    },
    getSelectionState: () => ({
      selectedNodes: modelStore?.selectedNodeIds || [],
      selectedBeams: modelStore?.selectedBeamIds || [],
      lastSelectionBounds: (window as any).__lastSelectionBounds,
      totalSelected: (modelStore?.selectedNodeIds?.length || 0) + (modelStore?.selectedBeamIds?.length || 0)
    })
  };
};
